import { Features, selectHasFeature } from "@/store/features/features-slice";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { HiddenScans } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import { selectShowDynamicOverviewMap } from "@/store/view-options/view-options-selectors";
import {
  IElement,
  IElementDynamicTiledMap,
  IElementGenericImgSheet,
  IElementSection,
  isIElementDynamicTiledMap,
  isIElementOverviewImage,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectChildrenDepthFirst,
  selectDatasetOrArea,
  selectIElement,
  selectIElementChildren,
  selectIElementWorldPosition,
  selectIElementWorldTransform,
  selectRootId,
} from "@faro-lotv/project-source";
import { DynMapMetadata } from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { uniqBy } from "es-toolkit";
import { Matrix4, Matrix4Tuple, Vector3 } from "three";
import { encodeScanList } from "./tile-math";

/** Maps the dynamic map frame (Z-up, right handed) to the Project API frame (Y-up, left handed) */
const MAP_TO_PROJECT = new Matrix4().makeBasis(
  new Vector3(1, 0, 0),
  new Vector3(0, 0, 1),
  new Vector3(0, 1, 0),
);

/** Where a dynamic map is placed in the world */
export type DynamicMapPlacement = {
  /** Maps the map frame to the world */
  matrix: Matrix4Tuple;
  /** Elevation of the map plane in the map frame */
  elevation: number;
};

const selectAllDynamicMaps = createSelector(
  [(state: OnlyIElementsStore) => state.iElements.iElements],
  (iElements) => Object.values(iElements).filter(isIElementDynamicTiledMap),
);

/**
 * @returns the dynamic map generated together with a static overview map, if any
 * @param sheet the static overview map, possibly the copy placed in the area
 */
export function selectDynamicMapForSheet(sheet: IElementGenericImgSheet) {
  return (state: OnlyIElementsStore): IElementDynamicTiledMap | undefined => {
    if (!isIElementOverviewImage(sheet)) return;
    // The dynamic map is a sibling of the static one, the copy in the area shares its md5 hash
    return selectAllDynamicMaps(state).find(
      (map) =>
        map.parentId === sheet.parentId ||
        (!!sheet.md5Hash &&
          !!map.parentId &&
          selectIElementChildren(map.parentId)(state).some(
            (s) => isIElementOverviewImage(s) && s.md5Hash === sheet.md5Hash,
          )),
    );
  };
}

/**
 * @returns the dynamic map to render in place of a static overview map, if available and enabled
 * @param sheet the static overview map
 */
export function selectShownDynamicMap(sheet: IElementGenericImgSheet) {
  return (state: RootState): IElementDynamicTiledMap | undefined =>
    selectHasFeature(Features.DynamicOverviewMap)(state) &&
    selectShowDynamicOverviewMap(state)
      ? selectDynamicMapForSheet(sheet)(state)
      : undefined;
}

/**
 * @returns the placement in the world of a dynamic map
 * @param state of the app
 * @param sheet the static overview map the dynamic map replaces, used for its elevation
 */
export function computeDynamicMapPlacement(
  state: OnlyIElementsStore,
  sheet: IElementGenericImgSheet,
): DynamicMapPlacement {
  // The map tiles are in the project root frame, including its left-handed Z flip
  const matrix = new Matrix4()
    .fromArray(
      selectIElementWorldTransform(selectRootId(state))(state)
        .worldMatrixMixedHd,
    )
    .multiply(MAP_TO_PROJECT);

  // The map has no height, use the one of the static sheet
  const elevation = new Vector3(
    ...selectIElementWorldPosition(sheet.id)(state),
  ).applyMatrix4(matrix.clone().invert()).z;

  return { matrix: matrix.toArray(), elevation };
}

/**
 * @returns the compact list of the scans to render, undefined if all scans are hidden
 * @param metadata of the map
 * @param hiddenScans the scans hidden by the user
 */
export function computeDynamicMapScans(
  metadata: DynMapMetadata,
  hiddenScans: HiddenScans,
): string | undefined {
  const hidden = new Set(
    metadata.scans.flatMap(({ uuid }) =>
      uuid && hiddenScans[uuid] ? [uuid] : [],
    ),
  );
  // Scans without id can only be rendered as part of the full set
  if (!hidden.size) {
    return encodeScanList(metadata.scans.keys());
  }
  return encodeScanList(
    metadata.scans.flatMap(({ uuid }, index) =>
      uuid && !hidden.has(uuid) ? [index] : [],
    ),
  );
}

/**
 * @returns the dynamic overview maps of the area, of the data sets aligned to it,
 * and of the data set of the reference element
 * @param state current store state
 * @param area The active area
 * @param referenceElement The reference element of the scene, often a single scan
 */
export const selectAreaDynamicMaps = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, area?: IElementSection) => area,
      (
        state: OnlyIElementsStore,
        area?: IElementSection,
        referenceElement?: IElement,
      ) => referenceElement,
    ],
    computeAreaDynamicMaps,
  ),
);

function computeAreaDynamicMaps(
  iElements: IElementsState,
  area?: IElementSection,
  referenceElement?: IElement,
): IElementDynamicTiledMap[] {
  const state = { iElements };
  const dataSetIds = area
    ? (iElements.areaDataSets[area.id]?.map((ds) => ds.elementId) ?? [])
    : [];
  const parents = [
    area,
    // The reference element is often a single scan, while the map is a child of its data set
    referenceElement ? selectDatasetOrArea(referenceElement)(state) : undefined,
    ...dataSetIds.map((id) => selectIElement(id)(state)),
  ];
  return uniqBy(
    parents.flatMap((parent) =>
      selectChildrenDepthFirst(parent, isIElementDynamicTiledMap)(state),
    ),
    (map) => map.id,
  );
}
